import { selectIsGlobalSmsFirstPartyUIFeatureFlagOn } from 'core/userContext';
import { SMSContentDefinition } from 'modules/graphql/types';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { FormField } from 'ui/formField';

import { LabelWithLink, constructErrorInlineText } from '../../common';
import { SMSRestrictionText } from '../../common/RestrictionText';
import { TextAreaField } from '../../common/components';
import { FieldNames, MediumDataKey } from '../../common/types';
import { constructFieldErrors, getIsEditMode } from '../../common/utils';

import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';

export const SMS_MESSAGE: MediumDataKey = 'message';

export const MessageTextArea = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const optOutDisclaimer = smsData.optOutDisclaimer?.message || '';

  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const isGlobalSmsEnabled = useSelector(selectIsGlobalSmsFirstPartyUIFeatureFlagOn);

  /* Max length is 1600. However, because we want to separate opt-out disclaimer text and sms message,
   ** getFullMessage() will add \n\n. To account that, total length is 1598. When displayed, it will say 1600 (counting \n\n).
   */
  const MAX_MESSAGE_LENGTH = 1598 - optOutDisclaimer.length;

  const restrictionTextLabel = (
    <SMSRestrictionText
      textContent={smsData.message}
      useGSM
      hasMsgCount
      hasImageUrl={!!smsData.imageUrl}
    />
  );

  const HelpText = () => {
    if (!isEditModeVisible) return <></>;

    if (!optOutDisclaimer)
      return (
        <LabelWithLink
          openInNewTab
          text={SETTINGS_DISPLAY_CONTENT.message.disabled}
          url="https://support.iterable.com/hc/en-us/articles/211970843#sms-opt-out-instructions"
        />
      );

    if (isGlobalSmsEnabled)
      return (
        <LabelWithLink
          openInNewTab
          text={SETTINGS_DISPLAY_CONTENT.message.globalSmsHelpText}
          url="https://support.iterable.com/hc/en-us/articles/15521924837908"
        />
      );

    return <></>;
  };

  const { hasError: messageError, hasWarning: messageWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.MESSAGE,
  });

  const messageErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: messageError,
    hasWarning: messageWarning,
  });

  return (
    <FormField
      data-test="sms-message-form-field"
      errorText={messageErrorText}
      warningText={messageErrorText}
      hasError={messageError}
      hasWarning={messageWarning}
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.message.labelText}
      restrictionText={restrictionTextLabel}
      helpText={<HelpText />}
    >
      <TextAreaField
        hasError={messageError}
        mediumRef={messageRef}
        mediumDataKey={SMS_MESSAGE}
        mediumDataValue={smsData.message}
        hasSecondaryText
        onChangeHandler={(newMessage: string) => setCurrentMediumField(SMS_MESSAGE, newMessage)}
        maxCharacters={MAX_MESSAGE_LENGTH}
        isUnset={!!smsData.message}
      />
    </FormField>
  );
};
